import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { childrenApi, billingApi } from "../../api/endpoints.js";
import { AdultScreen, AdultHeader, Content } from "../../components/Layout.js";
import { Card } from "../../components/Card.js";
import { Button } from "../../components/Button.js";
import { Loader } from "../../components/Loader.js";
import { ErrorBanner, InfoBanner } from "../../components/Form.js";
import { useQuery } from "../../hooks/useQuery.js";
import { useT } from "../../i18n/index.js";
import { edadEnAnios } from "../../utils/edad.js";
import { irACheckout } from "../billing/checkout.js";
import styles from "./Parent.module.css";

export function ParentDashboard() {
  const t = useT();
  const navigate = useNavigate();
  const { data: ninos, loading, error, reload } = useQuery(() => childrenApi.listar());
  const creditos = useQuery(() => billingApi.misCreditos());
  const paquetes = useQuery(() => billingApi.productos());
  const [comprando, setComprando] = useState<string | null>(null);
  const [errorCompra, setErrorCompra] = useState<string | null>(null);

  async function comprar(codigo: string) {
    setErrorCompra(null);
    setComprando(codigo);
    try {
      await irACheckout(codigo);
    } catch (e) {
      setErrorCompra(e instanceof Error ? e.message : "No se pudo iniciar la compra.");
      setComprando(null);
    }
  }

  return (
    <AdultScreen>
      <AdultHeader title={t("parent.dashboard.title")} />
      <Content>
        {creditos.data ? (
          <Card>
            <h2>Evaluaciones</h2>
            {creditos.data.saldo > 0 ? (
              <InfoBanner tone={creditos.data.saldo <= 2 ? "warn" : "info"}>
                Te quedan <strong>{creditos.data.saldo}</strong> evaluaciones
                {creditos.data.saldo <= 2 ? " — considera comprar un paquete." : "."}
              </InfoBanner>
            ) : (
              <InfoBanner tone="warn">
                No te quedan evaluaciones. Compra un paquete para iniciar una nueva evaluación.
              </InfoBanner>
            )}
            {paquetes.data && paquetes.data.length > 0 ? (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 14, color: "var(--ink-soft)" }}>Paquetes disponibles:</div>
                {errorCompra ? <ErrorBanner message={errorCompra} /> : null}
                <div className={styles.paquetes}>
                  {paquetes.data.map((p) => (
                    <div key={p.id} className={styles.paquete}>
                      <div>
                        <b>{p.nombre}</b>
                        <div style={{ fontSize: 13, color: "var(--ink-soft)" }}>
                          {p.evaluacionesIncluidas ?? "∞"} evaluaciones · {p.precio} {p.moneda}
                          {p.vigenciaDias ? ` · vigencia ${p.vigenciaDias} días` : ""}
                        </div>
                      </div>
                      <Button
                        onClick={() => comprar(p.codigo)}
                        disabled={comprando !== null}
                      >
                        {comprando === p.codigo ? t("common.loading") : "Comprar"}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </Card>
        ) : null}

        {loading ? <Loader /> : null}
        {error ? (
          <>
            <ErrorBanner message={error} />
            <Button variant="ghost" onClick={reload}>
              {t("common.retry")}
            </Button>
          </>
        ) : null}

        {ninos && ninos.length === 0 ? (
          <Card>
            <p>{t("parent.dashboard.empty")}</p>
          </Card>
        ) : null}

        {ninos?.map((n) => (
          <Card key={n.id}>
            <div className={styles.kidChip}>
              <div className={styles.avatar}>🧒</div>
              <div>
                <b className={styles.kidName}>{n.nombre}</b>
                <span className={styles.kidMeta}>
                  {t("common.years", { n: edadEnAnios(n.fechaNacimiento) })}
                </span>
              </div>
            </div>
            <div className={styles.actions}>
              <Button
                onClick={() => navigate(`/padre/nino/${n.id}/consentimientos`)}
              >
                {t("parent.dashboard.startEval")}
              </Button>
              <Button variant="ghost" onClick={() => navigate(`/padre/nino/${n.id}/practica`)}>
                {t("parent.dashboard.viewPlans")}
              </Button>
              <Button variant="ghost" onClick={() => navigate(`/padre/nino/${n.id}/historial`)}>
                {t("history.link")}
              </Button>
            </div>
          </Card>
        ))}

        <Button variant="sun" block onClick={() => navigate("/padre/nuevo")}>
          + {t("parent.dashboard.addChild")}
        </Button>
        <Link to="/terapeutas" style={{ textAlign: "center", fontSize: 13, marginTop: 4 }}>
          {t("report.findTherapist")} →
        </Link>
      </Content>
    </AdultScreen>
  );
}
